import React, { CSSProperties } from "react";
import styled from "styled-components";
import { Controller, Scene } from "react-scrollmagic";
import { PlayState, Timeline, Tween } from "react-gsap";
import { logoEComicSP_K, logoEComicSR_K } from "../model/DataConst";
import { OrientationContext, OrientationEnum } from "../provider/OrientationProvider";
import { MAX_WIDTH, MAX_WIDTH_STR } from "../model/Const";
import { IUADevice, userAgentInfo } from "../model/UserAgentInfo";

const SummaryImg = styled.img`
	height: 6vh;
	margin-top: 10%;
	// margin-bottom: 5%;
	object-fit: contain;
`;

const SummaryP = styled.p`
	white-space: nowrap;
	// font-size: 2.5vw;
	// text-align: center;
`;

const Summary: React.FC = () => {

	// トリガーとなる要素
	const triggerRef = React.useRef<HTMLDivElement>( null );

	// トリガーとなるID
	const [triggerId, setTriggerId] = React.useState( "" );

	// 一回だけ実行
	React.useEffect( () => {
		setTriggerId( "#summary" );
	}, [] );

	// useContext
	const context = React.useContext( OrientationContext );

	// 最大幅を設定するスタイル（PCの場合のみ設定）
	const maxWidthStyle = React.useMemo( () => {
		// 302と2622と0.12はeComicScreen+の画像のサイズから算出した固定値
		const imgHeight = window.innerHeight * 0.06 / 302 * 2622 > window.innerWidth ?
			{ height: Math.floor( window.innerWidth * 0.9 * 0.11 ) + "px" } : undefined;

		const pStyle = context.orientation === OrientationEnum.landscape || window.innerWidth / window.innerHeight > 0.58 ?
			{
				marginBlockStart: ".1em",
				marginBlockEnd: ".1em"
			} : undefined;

		// 幅が最大幅より大きく、PCだったら最大幅を指定
		if ( window.innerWidth > MAX_WIDTH && userAgentInfo.device === IUADevice.pc ) {
			return {
				max: {
					maxWidth: MAX_WIDTH_STR,
					margin: "auto"
				} as CSSProperties,
				p: {
					...pStyle,
					fontSize: "1.5rem",
				} as CSSProperties,
				imgHeight: imgHeight,
			}
		}

		return {
			max: undefined,
			p: window.innerWidth < 330 ? // Chromeの場合は最小フォントサイズが10pxで、最大一行に33文字なので330px。これを下回る横幅だったら、折り返しを許可するようにする。
				{ whiteSpace: "normal", ...pStyle } : { ...pStyle },
			imgHeight: imgHeight
		};
	}, [context.resize.width] );

	return (
		<>
			<div id="summary" ref={triggerRef}/>
			<section
				id="summary-section"
				style={{
					position: "relative",
					overflow: "hidden",
					backgroundColor: "white",
					padding: "5vh 0 10vh 0",
					minHeight: "80vh"
				}}
				className={"mincho"}
			>
				{(() => {
					// トリガー要素の読み込み前はnow loadingを表示
					if ( triggerId === "" ) {
						return <>now loading...</>;
					}

					return <Controller>
						<Scene
							triggerHook={0.9}
							duration={1}
							triggerElement={triggerId}
						>
							{(
								progress: any,
								event: any
							) => (
								<Timeline
									wrapper={
										<section
											style={{
												...maxWidthStyle.max,
												fontSize: "2.5vw",
												textAlign: "center"
											}}/>
									}
									playState={
										(event.type === "enter" && event.scrollDirection === "FORWARD") ? PlayState.play :
											(event.type === "enter" && event.scrollDirection === "REVERSE") ? PlayState.reverse : undefined
									}
								>
									{/*{console.log(progress)}*/}
									{/*{console.log(event)}*/}

									{/*画像*/}
									<Tween
										duration={1.5}
										from={{
											css: {
												opacity: 0,
												rotationX: -90,
												transformOrigin: "50% top -100"
											},
											ease: "back.out(2)",
										}}
										to={{
											css: {
												opacity: 1,
												rotationX: 0
											},
											ease: "back.out(2)",
										}}
									>
										<h2 style={{
											marginBlockEnd: 0,
											width: "100%",
											display: "flex",
											justifyContent: "center"
										}}>
											<SummaryImg
												style={{
													maxWidth: "90%",
													...maxWidthStyle.imgHeight
												}}
												src={logoEComicSP_K}
												alt="eComicScreen+"/>
										</h2>
									</Tween>
									<Tween
										position={1}
										duration={2}
										from={{
											css: {
												opacity: 0,
												marginLeft: "10vw"
											},
											ease: "back.out(2)",
										}}
										to={{
											css: {
												opacity: 1,
												marginLeft: 0
											},
											ease: "back.out(2)",
										}}
										stagger={0.2}
									>
										<SummaryP style={maxWidthStyle.p}>電子コミックで発生するモアレを効果的に抑制し、高解像度画像を作成。</SummaryP>
										<SummaryP style={maxWidthStyle.p}>モアレに悩まされることなく、シャープでキレイな線やセリフを実現。</SummaryP>
										<SummaryP style={maxWidthStyle.p}>新刊電子コミックに最適</SummaryP>
									</Tween>
									<Tween
										position={2}
										duration={1}
										from={{
											css: {
												opacity: 0,
												rotationX: -90,
												transformOrigin: "50% top -100"
											},
											ease: "back.out(2)",
										}}
										to={{
											css: {
												opacity: 1,
												rotationX: 0
											},
											ease: "back.out(2)",
										}}
									>
										<h2 style={{
											marginBlockEnd: 0,
											marginBlockStart: "1em",
											width: "100%",
											display: "flex",
											justifyContent: "center"
										}}>
											<SummaryImg
												style={{
													maxWidth: "56%",
													...maxWidthStyle.imgHeight
												}}
												src={logoEComicSR_K}
												alt="eComicSR"/>
										</h2>
									</Tween>
									<Tween
										position={3}
										duration={1.5}
										from={{
											css: {
												opacity: 0,
												marginLeft: "10vw"
											},
											ease: "back.out(2)",
										}}
										to={{
											css: {
												opacity: 1,
												marginLeft: 0
											},
											ease: "back.out(2)",
										}}
										stagger={0.2}
									>
										<SummaryP style={maxWidthStyle.p}>これまで制作した電子コミックのデータをベースとした超解像技術。</SummaryP>
										<SummaryP style={maxWidthStyle.p}>電子コミックを再制作することなく高解像度化。</SummaryP>
										<SummaryP style={maxWidthStyle.p}>既刊電子コミックに最適</SummaryP>
									</Tween>
									<Tween
										position={4.5}
										duration={3}
										from={{
											css: {
												opacity: 0,
												rotationX: -90,
												transformOrigin: "50% top -100"
											},
											ease: "Strong.easeOut",
										}}
										to={{
											css: {
												opacity: 1,
												rotationX: 0
											},
											ease: "Strong.easeOut",
										}}
										stagger={0.5}
									>
										<h2 style={{ visibility: "hidden" }}>まとめ</h2>
										<SummaryP style={maxWidthStyle.p}>高画質化するスマートフォンやタブレット。</SummaryP>
										<SummaryP style={maxWidthStyle.p}>eComicシリーズを活用することで時代に最適化。</SummaryP>
										<SummaryP style={maxWidthStyle.p}>電子コミックはより価値のあるものへと生まれ変わります。</SummaryP>
									</Tween>
								</Timeline>
							)}
						</Scene>
					</Controller>;
				})()}
			</section>
			{/*{createSummary( triggerId, maxWidthStyle.max, maxWidthStyle.p, maxWidthStyle.imgHeight )}*/}
		</>
	);
};

export default Summary;